import React, { useState, useRef } from 'react';
import { Modal, Button, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { deleteStudiesFromClinical } from '../../view/studies/StudiesService';

const iconCircleStyle = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: '#fee2e2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const infoBoxStyle = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};
const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 };
const labelStyle = { color: '#6b7280' };
const valueStyle = { color: '#111827', fontWeight: 500 };

export function DeleteStudyDialog({ study, open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);

  // ── draggable modal support ──────────────────────────────────────────────
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    // Only drag when clicking the header area (not buttons inside it)
    if (e.target.closest('button')) return;
    dragState.current = { dragging: true, startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    const onMouseMove = (ev) => {
      if (!dragState.current.dragging) return;
      setPos({ x: ev.clientX - dragState.current.startX, y: ev.clientY - dragState.current.startY });
    };
    const onMouseUp = () => {
      dragState.current.dragging = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleDelete = async () => {
    if (!study) return;

    try {
      setLoading(true);

      const studyIds = [study.id].filter(Boolean);
      const studyUIDs = [study.studyInstanceUID].filter(Boolean);

      if (studyIds.length === 0) {
        message.error('No valid study IDs found for deletion');
        setLoading(false);
        return;
      }

      const response = await deleteStudiesFromClinical({
        study_ids: studyIds,
        study_instance_uids: studyUIDs,
      });

      const deletedCount = response?.data?.data?.deleted || 0;
      const failedCount = response?.data?.data?.failed || 0;

      if (deletedCount > 0) {
        message.success(
          `Successfully deleted ${deletedCount} ${
            deletedCount === 1 ? 'study' : 'studies'
          }${failedCount > 0 ? ` (${failedCount} failed)` : ''}`
        );
      } else {
        message.warning('No studies were deleted');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error deleting studies:', error);
      message.error(error.response?.data?.message || 'Failed to delete studies');
    } finally {
      setLoading(false);
    }
  };

  if (!study) return null;

  const imageCount = study.images && study.images !== 'N/A' ? study.images : null;

  return (
    <Modal
      title={null}
      visible={open}
      onCancel={onClose}
      modalRender={(modal) => (
        <div
          style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
        >
          {modal}
        </div>
      )}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancel
        </Button>,
        <Button key="delete" type="primary" danger onClick={handleDelete} loading={loading}>
          Delete Study
        </Button>,
      ]}
    >
      {/* Header with icon — drag handle */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, cursor: 'move', userSelect: 'none' }}
        onMouseDown={onMouseDown}
      >
        <div style={iconCircleStyle}>
          <ExclamationCircleOutlined style={{ color: '#dc2626', fontSize: 24 }} />
        </div>
        <span style={{ fontSize: 18, fontWeight: 600 }}>Delete Study</span>
      </div>

      <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
        Are you sure you want to permanently delete this study? This action cannot be undone.
      </p>

      <div style={infoBoxStyle}>
        <div style={gridStyle}>
          <div><span style={labelStyle}>Patient ID:</span></div>
          <div style={valueStyle}>{study.patientID}</div>
          <div><span style={labelStyle}>Patient:</span></div>
          <div style={valueStyle}>{study.patientName}</div>
          <div><span style={labelStyle}>Modality:</span></div>
          <div style={valueStyle}>{study.modality}</div>
          <div><span style={labelStyle}>Body Part:</span></div>
          <div style={valueStyle}>{study.bodyPart || 'N/A'}</div>
          <div><span style={labelStyle}>Study Date:</span></div>
          <div style={valueStyle}>{study.studyDate}</div>
          <div><span style={labelStyle}>Images:</span></div>
          <div style={valueStyle}>{imageCount != null ? `${imageCount} file${imageCount !== '1' ? 's' : ''}` : 'N/A'}</div>
        </div>
      </div>

      <p style={{ color: '#dc2626', fontWeight: 500, fontSize: 14 }}>
        ⚠ All images and associated data will be permanently deleted.
      </p>
    </Modal>
  );
}
